import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  review: "Review",
  lead: "Lead",
  photo_approved: "Photo approved",
  photo_rejected: "Photo update",
  private_message: "Private message",
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function useNotifications(user) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const load = useCallback(() => {
    axios.get(`${API}/notifications`)
      .then((r) => { setItems(r.data.items || []); setUnread(r.data.unreadCount || 0); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user, load]);
  return { items, load, unread };
}

function NotificationItems({ items, onOpen }) {
  if (items.length === 0) return <p className="text-gray-500 text-sm font-body text-center py-8" data-testid="notification-empty">No notifications yet — new reviews and leads on your listings show up here.</p>;
  return items.map((notification) => (
    <button key={notification.id} onClick={() => onOpen(notification)}
      className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${notification.isRead ? "opacity-60" : ""}`}
      data-testid={`notification-item-${notification.id}`}>
      <span className="flex items-center gap-2 mb-1"><span className="text-[10px] uppercase tracking-widest font-body font-bold text-brand-red">{TYPE_LABELS[notification.type] || "Update"}</span><span className="text-gray-500 text-[11px] font-body">{timeAgo(notification.createdAt)}</span></span>
      <span className="block min-w-0"><span className="block text-navy text-sm font-body font-semibold truncate">{notification.title}</span>{notification.body && <span className="block text-gray-500 text-xs font-body truncate">{notification.body}</span>}</span>
    </button>
  ));
}

export function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { items, load, unread } = useNotifications(user);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  if (!user) return null;

  const openItem = (n) => {
    setOpen(false);
    if (!n.isRead) axios.post(`${API}/notifications/${n.id}/read`).then(load).catch(() => {});
    if (n.link) navigate(n.link);
  };

  const markAll = () => axios.post(`${API}/notifications/read-all`).then(load).catch(() => {});

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
        className="relative text-gray-600 hover:text-navy px-3 min-h-[44px] flex items-center justify-center font-body text-sm font-semibold transition-colors"
        data-testid="notification-bell-btn">
        Updates
        {unread > 0 && (
          <span className="ml-1.5 bg-brand-red text-white text-[10px] font-body font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
            data-testid="notification-unread-count">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden z-[90]" data-testid="notification-dropdown">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <p className="text-navy text-xs font-body font-bold uppercase tracking-wider">Notifications</p>
            {unread > 0 && (
              <button onClick={markAll} className="text-brand-blue text-xs font-body hover:underline" data-testid="notification-mark-all-btn">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            <NotificationItems items={items} onOpen={openItem} />
          </div>
        </div>
      )}
    </div>
  );
}
